import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
} from '@mui/material';
import {
  CloudUpload,
  Schedule,
  Analytics,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Refresh,
  Download,
  AutoAwesome,
  Timeline,
  Assessment,
} from '@mui/icons-material';

interface PrimaveraIntegrationProps {
  onScheduleImported?: (data: any) => void;
  onAnalysisComplete?: (insights: any) => void;
}

interface ScheduleFile {
  id: string;
  name: string;
  size: number;
  uploadDate: string;
  status: 'processing' | 'completed' | 'error';
  type: 'xer' | 'xml' | 'mpp';
  projectCount: number;
  taskCount: number;
}

interface AIInsight {
  type: 'critical_path' | 'resource_conflict' | 'schedule_risk' | 'optimization';
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  recommendation: string;
  impact: string;
}

const PrimaveraIntegration: React.FC<PrimaveraIntegrationProps> = ({
  onScheduleImported,
  onAnalysisComplete,
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<ScheduleFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<ScheduleFile | null>(null);
  const [aiInsights, setAiInsights] = useState<AIInsight[]>([]);
  const [showInsightsDialog, setShowInsightsDialog] = useState(false);
  const [analysisType, setAnalysisType] = useState('comprehensive');

  // Mock uploaded files
  const mockFiles: ScheduleFile[] = [
    {
      id: '1',
      name: 'Construction_Project_Q1_2024.xer',
      size: 2.4,
      uploadDate: '2024-03-15',
      status: 'completed',
      type: 'xer',
      projectCount: 3,
      taskCount: 245,
    },
    {
      id: '2',
      name: 'Baseline_Schedule_v2.xml',
      size: 1.8,
      uploadDate: '2024-03-10',
      status: 'completed',
      type: 'xml',
      projectCount: 1,
      taskCount: 189,
    },
    {
      id: '3',
      name: 'Updated_Schedule_March.mpp',
      size: 3.1,
      uploadDate: '2024-03-20',
      status: 'processing',
      type: 'mpp',
      projectCount: 2,
      taskCount: 312,
    },
  ];

  // Mock AI insights
  const mockInsights: AIInsight[] = [
    {
      type: 'critical_path',
      severity: 'high',
      title: 'Critical Path Delay Risk',
      description: 'Foundation work is on the critical path and showing 15% delay risk based on weather patterns and resource availability.',
      recommendation: 'Consider adding backup resources or adjusting the schedule to account for potential weather delays.',
      impact: 'Could delay project completion by 8-12 days',
    },
    {
      type: 'resource_conflict',
      severity: 'medium',
      title: 'Resource Over-allocation',
      description: 'Electrical crew is over-allocated by 120% during weeks 15-18, creating a bottleneck.',
      recommendation: 'Redistribute electrical work or hire additional certified electricians for the peak period.',
      impact: 'May cause 5-7 day delay in electrical phase',
    },
    {
      type: 'schedule_risk',
      severity: 'medium',
      title: 'Material Delivery Dependencies',
      description: 'Steel delivery is scheduled with zero float, creating high risk for cascade delays.',
      recommendation: 'Add 3-day buffer for steel delivery or identify alternative suppliers.',
      impact: 'High risk of 10+ day delay if delivery is late',
    },
    {
      type: 'optimization',
      severity: 'low',
      title: 'Schedule Optimization Opportunity',
      description: 'Parallel execution of interior work could reduce overall timeline by 12 days.',
      recommendation: 'Reorganize interior tasks to run in parallel with exterior finishing work.',
      impact: 'Potential to complete project 12 days early',
    },
  ];

  React.useEffect(() => {
    setUploadedFiles(mockFiles);
  }, []);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    // Simulate file upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          
          // Add new file to the list
          const newFile: ScheduleFile = {
            id: Date.now().toString(),
            name: file.name,
            size: file.size / (1024 * 1024), // Convert to MB
            uploadDate: new Date().toISOString().split('T')[0],
            status: 'processing',
            type: file.name.endsWith('.xer') ? 'xer' : file.name.endsWith('.xml') ? 'xml' : 'mpp',
            projectCount: Math.floor(Math.random() * 5) + 1,
            taskCount: Math.floor(Math.random() * 300) + 100,
          };
          
          setUploadedFiles(prev => [newFile, ...prev]);
          
          // Simulate processing completion
          setTimeout(() => {
            setUploadedFiles(prev => 
              prev.map(f => f.id === newFile.id ? { ...f, status: 'completed' } : f)
            );
          }, 3000);
          
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  }, []);

  const handleAnalyzeSchedule = (file: ScheduleFile) => {
    setSelectedFile(file);
    setIsAnalyzing(true);
    
    // Simulate AI analysis
    setTimeout(() => {
      setAiInsights(mockInsights);
      setIsAnalyzing(false);
      setShowInsightsDialog(true);
      onAnalysisComplete?.(mockInsights);
    }, 3000);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'processing':
        return <Schedule color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <Schedule />;
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'critical_path':
        return <Timeline color="error" />;
      case 'resource_conflict':
        return <Warning color="warning" />;
      case 'schedule_risk':
        return <ErrorIcon color="error" />;
      case 'optimization':
        return <AutoAwesome color="success" />;
      default:
        return <Analytics />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* Upload Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Primavera Schedule Import
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload your Primavera P6 schedule files (.XER, .XML) or Microsoft Project files (.MPP) for AI-powered analysis
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUpload />}
              disabled={isUploading}
            >
              Upload Schedule File
              <input
                type="file"
                hidden
                accept=".xer,.xml,.mpp"
                onChange={handleFileUpload}
              />
            </Button>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Analysis Type</InputLabel>
              <Select
                value={analysisType}
                label="Analysis Type"
                onChange={(e) => setAnalysisType(e.target.value)}
              >
                <MenuItem value="comprehensive">Comprehensive Analysis</MenuItem>
                <MenuItem value="critical_path">Critical Path Analysis</MenuItem>
                <MenuItem value="resource_analysis">Resource Analysis</MenuItem>
                <MenuItem value="risk_assessment">Risk Assessment</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {isUploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Uploading file... {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
          
          {isAnalyzing && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome />
                AI is analyzing your schedule... This may take a few moments.
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Uploaded Files */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Uploaded Schedule Files
          </Typography>
          
          {uploadedFiles.length === 0 ? (
            <Typography color="text.secondary">
              No files uploaded yet. Upload a Primavera or Project file to get started.
            </Typography>
          ) : (
            <List>
              {uploadedFiles.map((file, index) => (
                <React.Fragment key={file.id}>
                  <ListItem
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Analytics />}
                          onClick={() => handleAnalyzeSchedule(file)}
                          disabled={file.status !== 'completed' || isAnalyzing}
                        >
                          Analyze
                        </Button>
                        <IconButton size="small">
                          <Download />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      {getStatusIcon(file.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{file.name}</Typography>
                          <Chip label={file.type.toUpperCase()} size="small" />
                          <Chip 
                            label={file.status} 
                            size="small" 
                            color={file.status === 'completed' ? 'success' : file.status === 'error' ? 'error' : 'warning'}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {file.size.toFixed(1)} MB • {file.projectCount} projects • {file.taskCount} tasks • Uploaded {file.uploadDate}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < uploadedFiles.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* AI Insights Dialog */}
      <Dialog 
        open={showInsightsDialog} 
        onClose={() => setShowInsightsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome color="primary" />
            AI Schedule Analysis Results
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Analysis completed for: {selectedFile?.name}
          </Typography>
          
          {aiInsights.map((insight, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {getInsightIcon(insight.type)}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {insight.title}
                    </Typography>
                    <Chip 
                      label={insight.severity.toUpperCase()} 
                      size="small" 
                      color={getSeverityColor(insight.severity) as any}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {insight.description}
                  </Typography>
                  <Typography variant="body2" color="primary.main" sx={{ mb: 1 }}>
                    <strong>Recommendation:</strong> {insight.recommendation}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Impact:</strong> {insight.impact}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInsightsDialog(false)}>Close</Button>
          <Button variant="contained" startIcon={<Download />}>
            Export Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrimaveraIntegration;